import React from "react";
import useEntity from "../../../hooks/useCutting";
import {
    Box, 
    Paper, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Link,
    TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import { CheckBoxOutlined as CheckBoxOutlinedIcon, CheckBoxOutlineBlankOutlined as CheckBoxOutlineBlankOutlinedIcon } from "@mui/icons-material";
import {ID} from "../../../../types/global.d";
import EntityListHeader from "./CuttingListHeader";
import EntityListSearch from "./CuttingListSearch";
import {useLocation} from "react-router";
import CuttingListPagination from "./CuttingListPagination";
import * as Api from "../../../apis/Cutting.api";
import { Cutting } from "../../../types/models/Cutting";
import { PrimaryCutting } from "../../../types/models/Cutting";
import { usePagination } from '../../../../share';
import {displayDateTime, displayDate, displayTime} from "../../../../utils/formatDate.ts";
import {percentageFormat, fixedFormat, currencyFormat} from "../../../../utils/numberFormat";

import LocationLink from "../../../../location/components/location/LocationDetail/LocationLink";
import LocationName from "../../../../location/components/location/LocationDetail/LocationName";
import ProviderLink from "../../../../timber/components/provider/ProviderDetail/ProviderLink";
import ProviderName from "../../../../timber/components/provider/ProviderDetail/ProviderName";
import ContractorLink from "../../../../timber/components/contractor/ContractorDetail/ContractorLink";
import ContractorName from "../../../../timber/components/contractor/ContractorDetail/ContractorName";
import ForestOwnerLink from "../../../../timber/components/forestOwner/ForestOwnerDetail/ForestOwnerLink";
import ForestOwnerName from "../../../../timber/components/forestOwner/ForestOwnerDetail/ForestOwnerName";
import { toStringDisplay as toStringCuttingState } from "../../../types/models/CuttingState.d";
import { toStringDisplay as toStringShipmentState } from "../../../types/models/ShipmentState.d";
import { toStringDisplay as toStringShipmentType } from "../../../types/models/ShipmentType.d";
import { toStringDisplay as toStringUserType } from "../../../types/models/UserType.d";
import { toStringDisplay as toStringCordwoodMeasurementType } from "../../../types/models/CordwoodMeasurementType.d";

import { PrimaryProvider } from "../../../../timber/types/models/Provider";
import { PrimaryContractor } from "../../../../timber/types/models/Contractor";
import { PrimaryForestOwner } from "../../../../timber/types/models/ForestOwner";
import {useTheme} from "@mui/material/styles";
import useSearchDebounce from "../../../../hooks/useSearchDebounce";
import CuttingListItemTools from "./CuttingListItemTools";
import {useStateSession} from "../../../../hooks/useAuth";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";

import useMediaQuery from '@mui/material/useMediaQuery';
import { Trans } from "@lingui/react/macro";
import {
    mdiLinkVariant,
} from "@mdi/js";
import {
    StyledFillNavLink,
    ToolbarTableCell,
    BigTableRow,
    BigTableHeaderRow,
    SmallTableRow,
    SmallTableHeaderRow,
    StyledTableCell,
    StyledMdiIcon,
} from "../../../../share/components/styled/Table";
import {OrderBy, OrderDirection} from "../../../../types/global.d";

type IProps = {
    showAdd: boolean,
    name?: string,
    providerId?: ID,
    contractorId?: ID,
    forestOwnerId?: ID,
    };

const CuttingListItem = (props: IProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    
    const session = useStateSession()
    const {showAdd, ...query} = props;

    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('Cutting')

    
    
    
    
    const [, setSearchInputValue, searchInputTerm] = useSearchDebounce();
    const [searchInputAdvanced, setSearchInputAdvanced] = React.useState(undefined);

    const [orderBys, setOrderBys] = React.useState<OrderBy[]>([]);

    const toggleOrder = React.useCallback((name: string) => {
        setOrderBys(prev => {
            const current = prev.find(item => item.name === name)
            return [
                {
                    name,
                    direction: current?.direction === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc
                },
                ...(prev.filter(item => item.name !== name))
            ]
        });
    }, []);

    const { data: dataSimple = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
    }, 
        isLoading: isLoadingSimple,
        isFetching: isFetchingSimple,
        refetch: refetchSimple,
    } =
          Api.useGetCuttingPresentationQuery({
                  list: { page, pageSize },
                  filter: {
                    providerIdRefs: (PrimaryProvider.fromQuery(query.providerId)) ? [ PrimaryProvider.fromQuery(query.providerId) ] : [],
                    contractorIdRefs: (PrimaryContractor.fromQuery(query.contractorId)) ? [ PrimaryContractor.fromQuery(query.contractorId) ] : [],
                    forestOwnerIdRefs: (PrimaryForestOwner.fromQuery(query.forestOwnerId)) ? [ PrimaryForestOwner.fromQuery(query.forestOwnerId) ] : [],
                    name: searchInputTerm || '',
                    nameOr: true,
                    orderBys,
                  },
              }, {skip: !!searchInputAdvanced})
    
    const { data: dataAdvanced = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
        }, 
        isLoading: isLoadingAdvanced,
        isFetching: isFetchingAdvanced,
        refetch: refetchAdvanced,
    } =
        Api.useGetCuttingAdvancedPresentationQuery({
                list: { page, pageSize },
                filter: {

                    providerIdRefs: (PrimaryProvider.fromQuery(query.providerId)) ? [ PrimaryProvider.fromQuery(query.providerId) ] : [],
                    contractorIdRefs: (PrimaryContractor.fromQuery(query.contractorId)) ? [ PrimaryContractor.fromQuery(query.contractorId) ] : [],
                    forestOwnerIdRefs: (PrimaryForestOwner.fromQuery(query.forestOwnerId)) ? [ PrimaryForestOwner.fromQuery(query.forestOwnerId) ] : [],
                    ...searchInputAdvanced,
                    orderBys,
                },
            }, {skip: !searchInputAdvanced})
    const {data, isLoading, isFetching, refetch} =
        searchInputAdvanced ? 
            {
                data: dataAdvanced,
                isLoading: isLoadingAdvanced,
                isFetching: isFetchingAdvanced,
                refetch: refetchAdvanced,
            } 
            :
        {
                data: dataSimple,
                isLoading: isLoadingSimple,
                isFetching: isFetchingSimple,
                refetch: refetchSimple,
            }

    const routeLocation = useLocation();
    
    const onClickUrl = (entity: Cutting) => routeLocation.pathname.endsWith('cuttings') ?
        `${PrimaryCutting.fromEntity(entity).toPath()}`
        :
        `cuttings/${PrimaryCutting.fromEntity(entity).toPath()}`

    const onEditUrl = (entity: Cutting) => `${onClickUrl(entity)}/edit`

    
    
    return React.useMemo(() => (
        <Box>
            <Paper>
                <EntityListHeader showAdd={showAdd} {...query}/>
                <EntityListSearch
                    setSearchInputValue={setSearchInputValue}
                    searchInputAdvanced={searchInputAdvanced} setSearchInputAdvanced={setSearchInputAdvanced}
                    query={query}
                    refetch={refetch}
                />
                <TableContainer>

                    

                    

                    

                    

                    
        
                    
                    {isSmallScreen ?
                        <Table stickyHeader data-testid="Cutting-entity-list" size="small">
                            <TableHead>
                                <SmallTableHeaderRow>
                                    
                                    
                                    

                                    
                            <StyledTableCell align="left">
                                
                                <TableSortLabel
                                    active={ orderBys.find((item) => item.name === 'name') }
                                    direction={ orderBys.find((item) => item.name === 'name')?.direction || 'asc' }
                                    onClick={() => toggleOrder('name')}
                                >
                                
                                    <Trans>Name</Trans>
                                
                                </TableSortLabel>
                                
                            </StyledTableCell>
                        
                    
                                    
                                    
                                </SmallTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <SmallTableRow
                                        hover={true}
                                        key={ PrimaryCutting.fromEntity(item).toPath() }
                                    >
                                        
                        
                        
                        
                        

                        <TableCell
                            scope="row"
                            align="left"
                        >
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                                <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                    { item.name ?
                                        <>
                                            
                        
                        { item.name || '-' }
                        
                    
                                        </>
                                        :
                                        '-'
                                    }
                                </Typography>
                                
                            
                                <Typography component="div" variant="body1">
                                    { item.state &&
                                            
                                <>
                                    <Trans>State</Trans>:&nbsp;
                                    
                                    
                        
                        { toStringCuttingState(item.state) || '-' }
                        
                    
                                </>
                                
                                }
                                </Typography>
                                
                            
                            </Link>
                        </TableCell>

                    
                        
                    
                        
                    
                                    </SmallTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        :
                    
                        <Table stickyHeader data-testid="Cutting-entity-list" size="small">
                            <TableHead>
                                <BigTableHeaderRow>
                                    
                                    
                                    
                                    <StyledTableCell align="left">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'name') }
                                            direction={ orderBys.find((item) => item.name === 'name')?.direction || 'asc' }
                                            onClick={() => toggleOrder('name')}
                                        >
                                        
                                            <Trans>Name</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'state') }
                                            direction={ orderBys.find((item) => item.name === 'state')?.direction || 'asc' }
                                            onClick={() => toggleOrder('state')}
                                        >
                                        
                                            <Trans>State</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'startAt') }
                                            direction={ orderBys.find((item) => item.name === 'startAt')?.direction || 'asc' }
                                            onClick={() => toggleOrder('startAt')}
                                        >
                                        
                                            <Trans>Start At</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'endAt') }
                                            direction={ orderBys.find((item) => item.name === 'endAt')?.direction || 'asc' }
                                            onClick={() => toggleOrder('endAt')}
                                        >
                                        
                                            <Trans>End At</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    { query.contractorId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'contractorId') }
                                                direction={ orderBys.find((item) => item.name === 'contractorId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('contractorId')}
                                            >
                                            
                                                <Trans>Contractor</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    { query.forestOwnerId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'forestOwnerId') }
                                                direction={ orderBys.find((item) => item.name === 'forestOwnerId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('forestOwnerId')}
                                            >
                                            
                                                <Trans>Forest Owner</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                    
                                </BigTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryCutting.fromEntity(item).toPath() }
                                    >
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { item.name || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { toStringCuttingState(item.state) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {displayDateTime(item.startAt) }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {displayDateTime(item.endAt) }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                        { query.contractorId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <ContractorLink id={ item.contractorId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        { query.forestOwnerId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <ForestOwnerLink id={ item.forestOwnerId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        
                                        
                                        <ToolbarTableCell align="center" width="15%">
                                            <CuttingListItemTools data={ item }
                                                                               onClickUrl={onClickUrl(item)} onEditUrl={onEditUrl(item)}
                                                                                />
                                        </ToolbarTableCell>
                                    </BigTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    }
                    
            
                    { !data.totalItems && !isLoading && !isFetching &&
                        <Typography
                            sx={ { flex: '1 1 100%', padding: "2em", } }
                            component="div"
                        >
                            <Trans>No Cuttings</Trans>
                        </Typography>
                    }
                    { (isLoading || isFetching) &&
                        <GradientCircularProgress/>
                    }
                </TableContainer>
                <CuttingListPagination
                    totalItems={ data.totalItems }
                    page={Number(page)}
                    pageSize={pageSize}
                    handleChangePage={handleChangePage}
                    handleChangePageSize={handleChangePageSize}
                />
            </Paper>
        </Box>
    ), [isLoading, isFetching,isSmallScreen, data, page, pageSize, onClickUrl,  onEditUrl])
}

export default CuttingListItem;
